import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { consentFormsAPI, ConsentForm } from '../services/api';
import {
  User,
  ArrowLeft,
  Search,
  Loader,
  AlertCircle,
  Calendar,
  FileText,
  Download,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const PatientHistory: React.FC = () => {
  const navigate = useNavigate();
  const [documento, setDocumento] = useState('');
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [downloadingPDF, setDownloadingPDF] = useState<string | null>(null);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getTipo = (form: ConsentForm) => {
    const ac = form.consent_responses?.accepts_consent;
    if (ac === true) return { label: 'Consentimiento', color: 'text-green-700 bg-green-100' };
    if (ac === false) return { label: 'Disentimiento', color: 'text-red-700 bg-red-100' };
    return { label: 'N/A', color: 'text-gray-700 bg-gray-100' };
  };

  const handleBuscar = async () => {
    const doc = documento.trim();
    if (!doc) {
      setError('Ingresa el número de documento del paciente.');
      return;
    }
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const data = await consentFormsAPI.getAll(doc);
      setForms(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar el historial.');
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (formId: string, patientName: string, patientDoc: string) => {
    setDownloadingPDF(formId);
    try {
      const blob = await consentFormsAPI.downloadPDF(formId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (patientName || 'documento').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
      link.download = `Consentimiento_${safeName}_${patientDoc || formId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Error al descargar el PDF.');
    } finally {
      setDownloadingPDF(null);
    }
  };

  const patientName = forms.length > 0 ? forms[0].patient_data?.nombre : null;

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-5xl mx-auto">
        <div className="page-hero mb-2">
          <button type="button" onClick={() => navigate('/templates')} className="btn-secondary mb-4 py-2 text-sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a plantillas
          </button>
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-700 text-white shadow-md">
              <User className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
                Consulta clínica
              </p>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Historial por paciente</h1>
              <p className="mt-1 max-w-2xl text-sm text-slate-600 sm:text-base">
                Consentimientos y disentimientos asociados a un documento de identidad.
              </p>
            </div>
          </div>
        </div>

        <div className="card mb-6">
          <label className="label">Número de documento del paciente</label>
          <div className="flex flex-wrap gap-3 items-end">
            <input
              type="text"
              value={documento}
              onChange={(e) => { setDocumento(e.target.value); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
              placeholder="Ej: 1070012680"
              className="input-field flex-1 min-w-[200px]"
              disabled={loading}
            />
            <button
              type="button"
              onClick={handleBuscar}
              disabled={loading || !documento.trim()}
              className="btn-primary flex items-center gap-2"
            >
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
              Buscar
            </button>
          </div>
        </div>

        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {searched && !loading && (
          <>
            {patientName && (
              <p className="text-gray-700 font-medium mb-2">
                Paciente: <span className="text-gray-900">{patientName}</span> — Documento: {documento.trim()}
              </p>
            )}
            {forms.length === 0 ? (
              <div className="card text-center py-12">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No se encontraron consentimientos para este documento.</p>
              </div>
            ) : (
              <div className="card overflow-hidden p-0">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Plantilla</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Llenado por</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">PDF</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {forms.map((form) => {
                        const tipo = getTipo(form);
                        const pName = form.patient_data?.nombre || 'N/A';
                        const pDoc = form.patient_data?.documento || '';
                        return (
                          <tr key={form.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                {formatDate(form.filled_at)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-800 max-w-xs truncate" title={form.template_title || ''}>
                              {form.template_title || 'N/A'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${tipo.color}`}>
                                {form.consent_responses?.accepts_consent === true && <CheckCircle className="w-3.5 h-3.5" />}
                                {form.consent_responses?.accepts_consent === false && <XCircle className="w-3.5 h-3.5" />}
                                {tipo.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{form.filled_by || '—'}</td>
                            <td className="px-4 py-3 text-right">
                              {form.id && (
                                <button
                                  type="button"
                                  onClick={() => form.id && downloadPDF(form.id, pName, pDoc)}
                                  disabled={downloadingPDF === form.id}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                                >
                                  {downloadingPDF === form.id ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                  PDF
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PatientHistory;
